import React, { useState, useEffect } from 'react';
import { PageShell } from './layout';
import { GAME_CONFIG } from '../games';
import legalStyles from '../styles/Legal.module.css';
import { safeLocalStorageSetItem } from '../utils/safeStorage';

const clearLocalData = () => {
  try {
    const keysToRemove = [
      'user-id',
      'username',
      'email',
      'email-contact-consent',
      'language-level',
      'analytics-consent',
      'consent-preferences-v2',
      'consent-modal-snooze-until',
      'newsletter-signup-completed',
      'newsletter-signup-dismissed',
      'streak_data',
      'user-sync-last-run',
    ];
    const gamePrefixes = Object.values(GAME_CONFIG).map((game) => `${game.id}_`);

    // Remove known keys
    keysToRemove.forEach((k) => localStorage.removeItem(k));

    // Remove game state keys
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (gamePrefixes.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    }
  } catch (_) {
    // ignore
  }
};

const Privacy = () => {
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem('policy-lang');
      if (stored === 'it' || stored === 'en') return stored;
    } catch (_) {}
    const navLangs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ''])
      .filter(Boolean)
      .map(l => String(l).toLowerCase());
    const isItalian = navLangs.some(l => l.startsWith('it'));
    return isItalian ? 'it' : 'en';
  });
  const [deleted, setDeleted] = useState(false);
  const [userId, setUserId] = useState(() => localStorage.getItem('user-id') || '');

  useEffect(() => {
    safeLocalStorageSetItem('policy-lang', lang);
  }, [lang]);

  const toggle = () => setLang((l) => (l === 'it' ? 'en' : 'it'));

  const handleDelete = () => {
    clearLocalData();
    setDeleted(true);
    // Hard reload to ensure a fresh anonymous identity is generated and no state persists
    setTimeout(() => window.location.replace('/'), 800);
  };

  return (
    <PageShell showStickyAdRail={false}>
      <div style={{ maxWidth: 820, margin: '16px auto', padding: '0 16px', textAlign: 'justify' }} className={legalStyles.legalContent}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12 }}>
          <button onClick={toggle} style={{ border: '1px solid #ddd', borderRadius: 20, padding: '6px 12px', background: '#fff', cursor: 'pointer' }}>
            {lang === 'it' ? 'English' : 'Italiano'}
          </button>
        </div>

        {lang === 'en' ? (
          <>
            <h1>Giochini di Parole — Privacy Policy</h1>
            <p><strong>Effective date:</strong> April 22, 2026</p>
            

            <h2>1) Who we are and scope</h2>
            <p>“Giochini di Parole,” “we,” “us,” or “our” operates the Service. This Policy applies to information collected through the Service and related communications.</p>

            <h2>2) Information we collect</h2>
            <p><strong>Account and Login Data:</strong></p>
            <ul>
              <li>When you create an account, we collect <strong>email</strong>, <strong>username</strong>, and <strong>password</strong>. If you use <strong>Google login</strong>, we receive information such as your <strong>email</strong>, <strong>first and last name</strong>, and profile basics as permitted by your settings.</li>
            </ul>
            <p><strong>Gameplay and Usage Data:</strong></p>
            <ul>
              <li>We collect data necessary to provide the game experience (e.g., games played, completions, scores, tokens earned/spent, anti‑cheat flags).</li>
            </ul>
            <p><strong>Device and Technical Data:</strong></p>
            <ul>
              <li>IP address, device/browser type, language, operating system, referring URLs, and similar diagnostic data.</li>
            </ul>
            <p><strong>Cookies and Similar Technologies:</strong></p>
            <ul>
              <li>We use essential cookies to operate and secure the Service. We may also use analytics cookies and, where advertising is enabled, third-party advertising cookies or similar technologies to measure ad delivery, limit invalid traffic, and personalize or report on ads as described below.</li>
            </ul>
            <p><strong>Support Communications:</strong></p>
            <ul>
              <li>If you contact us, we collect the information you provide (e.g., email address and message content).</li>
            </ul>

            <h2>3) Analytics</h2>
            <ul>
              <li>For users in the EEA/UK, our consent module offers purpose-based controls for <strong>Necessary</strong>, <strong>Functional</strong>, <strong>Analytics</strong> (including session replay), and <strong>Marketing</strong> categories.</li>
              <li>Necessary cookies stay enabled to run the service. Optional categories are off by default in the EEA/UK until you choose otherwise.</li>
              <li>We use <strong>Google Analytics</strong> and <strong>PostHog</strong> only where analytics consent is enabled. If you decline or withdraw consent, analytics collection and session replay are disabled for your session.</li>
              <li>Where advertising is enabled, we may display ads served by <strong>Google AdSense</strong>. AdSense may use cookies or similar technologies to serve, measure, and report on ads, and to help detect invalid traffic.</li>
              <li>You can update your choices at any time from <strong>Manage cookies</strong> in settings/footer.</li>
              <li>For advertising in the EEA, UK, and Switzerland, Google's consent message appears before our site-preferences screen when a decision is needed. Advertising choices are separate from analytics, session replay, and promotional communications. Use <strong>Consenso pubblicitario</strong> in the footer or account settings to change advertising choices; changing them reloads the page.</li>
            </ul>

            <h2>4) Why we process your information (Purposes)</h2>
            <ul>
              <li>Provide, maintain, and improve the Service and game features;</li>
              <li>Authenticate users and secure accounts;</li>
              <li>Detect, investigate, and prevent cheating, fraud, and abuse;</li>
              <li>Measure performance and fix bugs;</li>
              <li>Communicate with you about the Service, updates, and policy changes;</li>
              <li>Comply with legal obligations and enforce our Terms.</li>
            </ul>

            <h2>5) Legal bases for processing (GDPR/UK GDPR)</h2>
            <ul>
              <li><strong>Contract</strong> (Art. 6(1)(b)) to provide the Service you request;</li>
              <li><strong>Legitimate interests</strong> (Art. 6(1)(f)) such as security, anti‑fraud, service improvement, and non‑invasive analytics outside the EEA/UK where permitted;</li>
              <li><strong>Consent</strong> (Art. 6(1)(a)) for optional analytics in the EEA/UK and for promotional communications where applicable;</li>
              <li><strong>Legal obligation</strong> (Art. 6(1)(c)) where applicable.</li>
            </ul>

            <h2>6) How we share information</h2>
            <ul>
              <li><strong>Service providers</strong> (e.g., authentication providers like Google; analytics providers like Google Analytics and PostHog; advertising providers like Google AdSense where ads are enabled; hosting and infrastructure providers) who process data on our behalf under appropriate agreements;</li>
              <li><strong>Legal and safety</strong> recipients, if required by law, subpoena, or to protect rights, safety, and the integrity of the Service;</li>
              <li><strong>Business transfers</strong>, in connection with a merger, acquisition, or asset sale.<br/>We do <strong>not</strong> sell your personal information. We do <strong>not</strong> share it for cross‑context behavioral advertising.</li>
            </ul>

            <h2>7) International transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your own. Where required, we use appropriate safeguards (e.g., Standard Contractual Clauses) to protect your information in cross‑border transfers.</p>

            <h2>8) Data retention</h2>
            <ul>
              <li>Account data: retained while your account is active and for up to <strong>24 months</strong> after closure (unless a longer period is required by law);</li>
              <li>Gameplay logs and anti‑cheat data: typically <strong>12–24 months</strong>;</li>
              <li>Analytics data (where enabled): according to our analytics tool configurations;</li>
              <li>Aggregated or de‑identified data may be retained without limitation.</li>
            </ul>

            <h2>9) Your rights</h2>
            <p>Depending on your location, you may have rights to access, correct, delete, restrict, or object to processing of your personal information, and to data portability. Where processing is based on consent, you may <strong>withdraw consent</strong> at any time. You also have the right to lodge a complaint with your local data protection authority.</p>
            <p><strong>California residents (CCPA/CPRA):</strong> You may request to know, access, correct, or delete personal information, and to limit use of sensitive personal information (if any). We do <strong>not</strong> sell or share your personal information for cross‑context behavioral advertising. We honor <strong>Global Privacy Control (GPC)</strong> signals where applicable.</p>
            <p>To exercise rights, contact us at <strong><a href="mailto:replies@giochinidiparole.com">replies@giochinidiparole.com</a></strong>. We may need to verify your request. To request account deletion, email <a href="mailto:replies@giochinidiparole.com">replies@giochinidiparole.com</a>.</p>

            <h2>10) Security</h2>
            <p>We implement reasonable technical and organizational measures to protect personal information. No method of transmission or storage is 100% secure.</p>

            <h2>11) Children’s privacy</h2>
            <p>The Service is not intended for children under <strong>13</strong>. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided personal information, contact us to request deletion.</p>

            <h2>12) Changes to this Policy</h2>
            <p>We may update this Policy from time to time. When we do, we will update the “Effective date” above. <strong>Your continued use of the Service after the effective date signifies your acceptance of the updated Policy.</strong></p>

            <h2>13) Contact</h2>
            <p>Questions or requests: <strong><a href="mailto:replies@giochinidiparole.com">replies@giochinidiparole.com</a></strong>.</p>
          </>
        ) : (
          <>
            <h1>Giochini di Parole — Informativa sulla Privacy</h1>
            <p><strong>Data di entrata in vigore:</strong> 22 aprile 2026</p>
            

            <h2>1) Chi siamo e ambito di applicazione</h2>
            <p>“Giochini di Parole”, “noi”, “ci” o “nostro” gestisce il Servizio. La presente Informativa si applica alle informazioni raccolte tramite il Servizio e le comunicazioni correlate.</p>

            <h2>2) Dati che raccogliamo</h2>
            <p><strong>Dati di account e accesso:</strong></p>
            <ul>
              <li>Quando crei un account, raccogliamo <strong>email</strong>, <strong>nome utente</strong> e <strong>password</strong>. Se utilizzi l'accesso con <strong>Google</strong>, riceviamo informazioni quali <strong>email</strong>, <strong>nome e cognome</strong> e dati di profilo di base, secondo le tue impostazioni.</li>
            </ul>
            <p><strong>Dati di gioco e utilizzo:</strong></p>
            <ul>
              <li>Raccogliamo i dati necessari a fornire l'esperienza di gioco (ad es. partite giocate, completamenti, punteggi, Gettoni guadagnati/spesi, segnali anti‑cheat).</li>
            </ul>
            <p><strong>Dati tecnici e del dispositivo:</strong></p>
            <ul>
              <li>Indirizzo IP, tipo di dispositivo/browser, lingua, sistema operativo, URL di provenienza e dati diagnostici simili.</li>
            </ul>
            <p><strong>Cookie e tecnologie simili:</strong></p>
            <ul>
              <li>Usiamo cookie essenziali per far funzionare e proteggere il Servizio. Possiamo usare anche cookie di analitica e, quando la pubblicita è abilitata, cookie pubblicitari di terze parti o tecnologie simili per misurare l'erogazione degli annunci, limitare il traffico non valido e personalizzare o rendicontare gli annunci come descritto di seguito.</li>
            </ul>
            <p><strong>Comunicazioni di supporto:</strong></p>
            <ul>
              <li>Se ci contatti, raccogliamo le informazioni fornite (es. indirizzo email e contenuto del messaggio).</li>
            </ul>

            <h2>3) Analitiche</h2>
            <ul>
              <li>Per utenti nello SEE/UE e nel Regno Unito, il modulo consenso offre controlli per categorie: <strong>Necessari</strong>, <strong>Funzionali</strong>, <strong>Analitici</strong> (incluso session replay) e <strong>Marketing</strong>.</li>
              <li>I cookie necessari restano sempre attivi. Le categorie opzionali sono disattivate di default nello SEE/UE/UK finché non esprimi una scelta.</li>
              <li>Utilizziamo <strong>Google Analytics</strong> e <strong>PostHog</strong> solo con consenso analitico attivo. Se rifiuti o revochi il consenso, analitica e session replay vengono disabilitati per la sessione.</li>
              <li>Quando la pubblicita è abilitata, possiamo mostrare annunci erogati da <strong>Google AdSense</strong>. AdSense può utilizzare cookie o tecnologie simili per erogare, misurare e rendicontare gli annunci e per contribuire a rilevare traffico non valido.</li>
              <li>Puoi modificare le preferenze in qualsiasi momento da <strong>Gestisci cookie</strong> in impostazioni/footer.</li>
              <li>Per la pubblicità nello SEE, nel Regno Unito e in Svizzera, il messaggio di consenso di Google precede le preferenze del sito quando serve una scelta. Le scelte pubblicitarie sono separate da analitiche, session replay e comunicazioni promozionali. Puoi modificarle da <strong>Consenso pubblicitario</strong> nel footer o nelle impostazioni dell'account; la modifica ricarica la pagina.</li>
            </ul>

            <h2>4) Finalità del trattamento</h2>
            <ul>
              <li>Fornire, mantenere e migliorare il Servizio e le funzioni di gioco;</li>
              <li>Autenticare gli utenti e proteggere gli account;</li>
              <li>Rilevare, indagare e prevenire cheating, frodi e abusi;</li>
              <li>Misurare le prestazioni e correggere bug;</li>
              <li>Comunicare aggiornamenti e modifiche alle policy;</li>
              <li>Adempiere a obblighi di legge e far valere i nostri Termini.</li>
            </ul>

            <h2>5) Basi giuridiche (GDPR/UK GDPR)</h2>
            <ul>
              <li><strong>Contratto</strong> (art. 6(1)(b)) per fornire il Servizio richiesto;</li>
              <li><strong>Legittimo interesse</strong> (art. 6(1)(f)) per sicurezza, antifrode, miglioramento del servizio e analitiche non invasive al di fuori dello SEE/UE/UK nei limiti consentiti;</li>
              <li><strong>Consenso</strong> (art. 6(1)(a)) per analitiche facoltative nello SEE/UE/UK e per comunicazioni promozionali ove applicabile;</li>
              <li><strong>Obbligo legale</strong> (art. 6(1)(c)) ove applicabile.</li>
            </ul>

            <h2>6) Condivisione delle informazioni</h2>
            <ul>
              <li><strong>Fornitori di servizi</strong> (ad es. fornitori di autenticazione come Google; fornitori di analitiche come Google Analytics e PostHog; fornitori pubblicitari come Google AdSense quando gli annunci sono abilitati; hosting e infrastruttura) che trattano i dati per nostro conto in base ad accordi idonei;</li>
              <li><strong>Autorità e sicurezza</strong>, se richiesto dalla legge, da un'ingiunzione o per proteggere diritti, sicurezza e integrità del Servizio;</li>
              <li><strong>Trasferimenti aziendali</strong>, in caso di fusione, acquisizione o cessione di beni.<br/>Non <strong>vendiamo</strong> i tuoi dati personali. Non li <strong>condividiamo</strong> per pubblicità cross‑context.</li>
            </ul>

            <h2>7) Trasferimenti internazionali</h2>
            <p>I tuoi dati possono essere trasferiti e trattati in Paesi diversi dal tuo. Ove richiesto, utilizziamo garanzie adeguate (es. <strong>Clausole Contrattuali Standard</strong>) per proteggere i dati nei trasferimenti transfrontalieri.</p>

            <h2>8) Conservazione dei dati</h2>
            <ul>
              <li>Dati di account: finché l'account è attivo e fino a <strong>24 mesi</strong> dopo la chiusura (salvo periodi più lunghi richiesti dalla legge);</li>
              <li>Log di gioco e dati anti‑cheat: in genere <strong>12–24 mesi</strong>;</li>
              <li>Dati di analitica (se attivi): secondo le configurazioni dello strumento di analitica;</li>
              <li>Dati aggregati o anonimizzati possono essere conservati senza limitazioni.</li>
            </ul>

            <h2>9) I tuoi diritti</h2>
            <p>In base alla tua località, potresti avere il diritto di accedere, correggere, cancellare, limitare o opporti al trattamento dei tuoi dati personali e alla portabilità. Quando il trattamento si basa sul consenso, puoi <strong>revocarlo</strong> in qualsiasi momento. Hai anche il diritto di proporre reclamo a un'autorità di controllo.</p>
            <p><strong>Residenti in California (CCPA/CPRA):</strong> puoi richiedere di conoscere, accedere, correggere o cancellare i dati personali e di limitare l'uso dei dati personali sensibili (se presenti). Non vendiamo né condividiamo i tuoi dati per pubblicità cross‑context. Rispettiamo i segnali <strong>Global Privacy Control (GPC)</strong> ove applicabile.</p>
            <p>Per esercitare i diritti, contattaci all'indirizzo <strong><a href="mailto:replies@giochinidiparole.com">replies@giochinidiparole.com</a></strong>. Potremmo dover verificare la tua richiesta. <strong>Per richiedere l'eliminazione dell'account, scrivi a <a href="mailto:replies@giochinidiparole.com">replies@giochinidiparole.com</a>.</strong></p>

            <h2>10) Sicurezza</h2>
            <p>Adottiamo misure tecniche e organizzative ragionevoli per proteggere i dati personali. Tuttavia, nessun metodo di trasmissione o conservazione è sicuro al 100%.</p>

            <h2>11) Privacy dei minori</h2>
            <p>Il Servizio non è destinato a minori di <strong>13 anni</strong>. Non raccogliamo consapevolmente dati personali di minori di 13 anni. Se ritieni che un minore di 13 anni ci abbia fornito dati personali, contattaci per richiederne la cancellazione.</p>

            <h2>12) Modifiche alla presente Informativa</h2>
            <p>Possiamo aggiornare periodicamente la presente Informativa. In tal caso aggiorneremo la <strong>Data di entrata in vigore</strong>. <strong>L'uso continuato del Servizio dopo tale data costituisce accettazione</strong> dell'Informativa aggiornata.</p>

            <h2>13) Contatti</h2>
            <p>Domande o richieste: <strong><a href="mailto:replies@giochinidiparole.com">replies@giochinidiparole.com</a></strong>.</p>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default Privacy;
